/* eslint-disable react/jsx-props-no-spreading */
import React, { useCallback, useContext, useMemo } from 'react';

import exportWithBoundary from '../../../../helpers/exportWithBoundary';
import useTranslations from '../../../../hooks/useTranslations';
import Pagination from '../../../Pagination';
import { Button, Text } from '../../../_atoms';
import colors from '../../../_foundations/colors.json';
import { HorizontalFlow } from '../../../_layouts';
import { JobMapContext } from '../JobMapContext';
import JobListItem from '../JobMapJobListItem';
import { crosshairs } from '../icons';
import useJobMapJobPostings from '../useJobMapJobPostings';
// import Button from '../../../Button';
import {
  IconWrap,
  JobListCategoryPicker,
  JobListContainer,
  JobListEmpty,
  JobListFilter,
  JobListFilters,
  JobListKeyword,
  JobListResults,
} from './styles';

const t = useTranslations('JobMapJobList');

function JobMapJobList() {
  const {
    categoryNameOptions,
    currentPlace,
    jobMapId,
    params,
    primaryColor,
    setParams,
    setZoomOutToJobs,
    hasRemoteJobs,
    zoomOutToJobs,
    zoomToAllJobs,
    zoomToUser,
  } = useContext(JobMapContext);

  const { data: jobData, isLoading: jobsLoading } = useJobMapJobPostings(
    jobMapId,
    {
      ...params,
      ...(currentPlace ? { geohash: currentPlace.geohash } : {}),
    }
  );

  const remoteJobsSelected = params.remote;
  const locationEnabled = 'geolocation' in navigator;

  const toggleRemoteJobs = useCallback(() => {
    if (remoteJobsSelected) {
      setParams((prev) => ({
        ...prev,
        remote: null,
      }));
    } else {
      setParams((prev) => ({
        ...prev,
        remote: true,
      }));
    }
  }, [setParams, remoteJobsSelected]);

  const clearFilters = useCallback(() => {
    setParams((prev) => ({
      ...prev,
      q: '',
      category_names: [],
      remote: null,
    }));
  }, [setParams]);

  const onKeywordChanged = useCallback(
    (keyword) => {
      setParams((prev) => ({
        ...prev,
        q: keyword,
        page: 1,
      }));
    },
    [setParams]
  );

  const onJobCategoriesChanged = useCallback(
    (categories) => {
      setParams((prev) => ({
        ...prev,
        category_names: categories,
        page: 1,
      }));
    },
    [setParams]
  );

  const setPage = useCallback(
    (newPage) => {
      setParams((prev) => ({
        ...prev,
        page: newPage,
      }));
    },
    [setParams]
  );

  const handleFindMe = useCallback(() => {
    setZoomOutToJobs(true);
    if ('geolocation' in navigator) {
      // check if geolocation is supported/enabled on current browser
      navigator.geolocation.getCurrentPosition(
        (position) => {
          // console.log('loc', position.coords.latitude, position.coords.longitude);
          zoomToUser(position.coords.latitude, position.coords.longitude);
        },
        () => {
          // console.log('loc error')
          setZoomOutToJobs(false);
        }
      );
    } else {
      // geolocation is not supported
      // get your location some other way
      // console.log('geolocation is not enabled on this browser')
      setZoomOutToJobs(false);
    }
  }, []);

  const renderFilters = useMemo(
    () => (
      <>
        <JobListFilter>
          <JobListKeyword
            initialValue={params.q}
            placeholder={t('job_maps.keyword_placeholder')}
            onChange={onKeywordChanged}
            aria-label={t('job_maps.keyword_placeholder')}
          />
        </JobListFilter>
        <JobListFilter>
          <JobListCategoryPicker
            id='job_categories'
            options={categoryNameOptions}
            placeholder={t('job_maps.categories_placeholder')}
            selectedOptions={params.category_names}
            onChange={(newValues) =>
              onJobCategoriesChanged(newValues.map((x) => x.value))
            }
          />
        </JobListFilter>
        {(locationEnabled || hasRemoteJobs) && (
          <JobListFilter
            style={{ display: 'flex', justifyContent: 'space-between' }}
          >
            <HorizontalFlow
              spacing='0.5rem'
              onClick={toggleRemoteJobs}
              style={{ cursor: 'pointer' }}
            >
              {hasRemoteJobs && (
                <>
                  {remoteJobsSelected ? (
                    <svg
                      xmlns='http://www.w3.org/2000/svg'
                      viewBox='0 0 448 512'
                      style={{
                        width: 20,
                        color: colors.aqua700,
                        fill: colors.aqua700,
                      }}
                    >
                      <path d='M400 480H48c-26.51 0-48-21.49-48-48V80c0-26.51 21.49-48 48-48h352c26.51 0 48 21.49 48 48v352c0 26.51-21.49 48-48 48zm-204.686-98.059l184-184c6.248-6.248 6.248-16.379 0-22.627l-22.627-22.627c-6.248-6.248-16.379-6.249-22.628 0L184 302.745l-70.059-70.059c-6.248-6.248-16.379-6.248-22.628 0l-22.627 22.627c-6.248 6.248-6.248 16.379 0 22.627l104 104c6.249 6.25 16.379 6.25 22.628.001z' />
                    </svg>
                  ) : (
                    <svg
                      xmlns='http://www.w3.org/2000/svg'
                      viewBox='0 0 448 512'
                      style={{
                        width: 20,
                        color: colors.neutral400,
                        fill: colors.neutral400,
                      }}
                    >
                      <path d='M400 32H48C21.5 32 0 53.5 0 80v352c0 26.5 21.5 48 48 48h352c26.5 0 48-21.5 48-48V80c0-26.5-21.5-48-48-48zm-6 400H54c-3.3 0-6-2.7-6-6V86c0-3.3 2.7-6 6-6h340c3.3 0 6 2.7 6 6v340c0 3.3-2.7 6-6 6z' />
                    </svg>
                  )}
                  <Text>{t('job_maps.remote_jobs_only')}</Text>
                </>
              )}
            </HorizontalFlow>
            {locationEnabled && (
              <Button
                buttonType='secondary'
                buttonStyle='ghost'
                onClick={handleFindMe}
                size='sm'
                disabled={zoomOutToJobs === true}
              >
                <IconWrap size={20} color='neutral-400'>
                  {crosshairs}
                </IconWrap>
                <Text>{t('job_maps.find_me')}</Text>
              </Button>
            )}
          </JobListFilter>
        )}
      </>
    ),
    [params, zoomOutToJobs]
  );

  const renderResults = useMemo(() => {
    if (jobsLoading) return null;
    if (jobData && jobData.meta) {
      if (jobData.meta.total_count === 0) {
        const filtered =
          params.q.length > 0 || params.category_names.length > 0;
        const cta = filtered ? (
          <Button buttonType='secondary' onClick={() => clearFilters()}>
            <Text>{t('job_maps.clear_filters')}</Text>
          </Button>
        ) : (
          <Button buttonType='secondary' onClick={() => zoomToAllJobs()}>
            <Text>{t('job_maps.show_all')}</Text>
          </Button>
        );
        return (
          <JobListEmpty>
            <img
              src='https://static.careerarc.com/polaris/job_maps/maps_no-job-matches_1.jpg'
              alt='no jobs'
            />
            <p>
              {filtered
                ? t('job_maps.empty_filtered')
                : t('job_maps.empty_default')}
            </p>
            {cta}
          </JobListEmpty>
        );
      }
      return (
        <>
          {jobData.entries.map((job) => (
            <JobListItem key={job.id} jobPosting={job} />
          ))}
          <Pagination
            total={jobData.meta.total_count}
            page={params.page}
            perPage={params.per_page}
            setPage={setPage}
            maxPage={10}
          />
        </>
      );
    }
    return null;
  }, [jobData, params]);

  return (
    <JobListContainer>
      <JobListFilters>{renderFilters}</JobListFilters>
      <JobListResults primaryColor={primaryColor}>
        {renderResults}
      </JobListResults>
    </JobListContainer>
  );
}

function JobMapJobListFallback() {
  return <JobListContainer />;
}

export default exportWithBoundary(JobMapJobList, JobMapJobListFallback);
